import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ScrollToTop } from '../components/ScrollToTop';
import { PortalHeader, PortalLoading } from '../components/PortalComponents';
import { BillingService } from '../services/mockServices';
import { 
  FileText, Download, Calendar, 
  CheckCircle, Clock, AlertCircle, CreditCard, Receipt,
  Building, MessageCircle
} from 'lucide-react';

function StatusBadge({ status }) {
  const config = {
    paid: { label: 'Pagado', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
    pending: { label: 'Pendiente', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
    overdue: { label: 'Vencido', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertCircle }
  };
  const c = config[status] || config.pending;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${c.color}`}>
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  );
}

function InvoiceRow({ invoice }) {
  const handleDownload = () => {
    alert('Descarga simulada: ' + invoice.number + '.pdf\n\nEsta función estará disponible con la integración Odoo.');
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
          <FileText className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-white font-['Outfit']">{invoice.number}</p>
          <p className="text-xs text-zinc-500 font-['Manrope']">{invoice.concept}</p>
        </div>
      </div>
      
      <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6">
        <div className="text-right">
          <p className="text-sm font-medium text-white">{invoice.amount.toFixed(2)}€</p>
          <p className="text-xs text-zinc-500">{new Date(invoice.date).toLocaleDateString('es-ES')}</p>
        </div>
        <StatusBadge status={invoice.status} />
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDownload}
          className="text-zinc-400 hover:text-white hover:bg-white/5"
        >
          <Download className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default function BillingPage() {
  const [invoices, setInvoices] = useState([]);
  const [nextBill, setNextBill] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [invoiceData, billData] = await Promise.all([
          BillingService.getInvoices(),
          BillingService.getNextBill()
        ]);
        setInvoices(invoiceData);
        setNextBill(billData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const pendingInvoices = invoices.filter(inv => inv.status === 'pending' || inv.status === 'overdue');
  const paidInvoices = invoices.filter(inv => inv.status === 'paid');

  if (loading) return <PortalLoading />;

  return (
    <div className="min-h-screen bg-[#030303]">
      <PortalHeader />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-light text-white font-['Outfit']">Facturación</h1>
          <p className="text-sm text-zinc-500 mt-1 font-['Manrope']">Consulta y descarga tus facturas</p>
        </div>

        {/* Next Bill Card */}
        {nextBill && (
          <div className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/20">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-amber-400/70 mb-2 font-['Manrope']">Próxima factura</p>
                <p className="text-3xl font-light text-white font-['Outfit']">{nextBill.amount.toFixed(2)}€</p>
                <div className="flex items-center gap-2 mt-2 text-sm text-zinc-400">
                  <Calendar className="w-4 h-4" />
                  <span className="font-['Manrope']">{new Date(nextBill.dueDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
              </div>
              <div className="w-14 h-14 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Receipt className="w-7 h-7 text-amber-400" />
              </div>
            </div>
          </div>
        )}

        {/* Payment Methods Info */}
        <div className="mb-8 p-4 rounded-xl bg-white/[0.02] border border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-zinc-500" />
              <div>
                <p className="text-sm text-white font-['Outfit']">Domiciliación bancaria activa</p>
                <p className="text-xs text-zinc-500 font-['Manrope']">Tus facturas se cobran automáticamente cada mes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bank Account Change Request */}
        <div className="mb-8 p-5 rounded-xl bg-gradient-to-r from-amber-500/[0.05] to-transparent border border-amber-500/20">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Building className="w-5 h-5 text-amber-400" />
              <div>
                <p className="text-sm text-white font-['Outfit']">¿Necesitas cambiar tus datos bancarios?</p>
                <p className="text-xs text-zinc-500 font-['Manrope']">Solicita la actualización de tu cuenta de domiciliación</p>
              </div>
            </div>
            <a 
              href="https://wa.me/34623243967?text=Hola,%20me%20gustaría%20solicitar%20un%20cambio%20de%20datos%20bancarios%20para%20mi%20cuenta%20Manklar."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-sm font-medium transition-colors"
              data-testid="bank-change-btn"
            >
              <MessageCircle className="w-4 h-4" />
              Solicitar cambio
            </a>
          </div>
        </div>

        {/* Pending Invoices */}
        {pendingInvoices.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xs uppercase tracking-wider text-zinc-500 mb-4 font-['Manrope']">Pendientes de pago</h2>
            <div className="space-y-3">
              {pendingInvoices.map(invoice => (
                <InvoiceRow key={invoice.id} invoice={invoice} />
              ))}
            </div>
          </div>
        )}

        {/* Paid Invoices */}
        <div className="mb-8">
          <h2 className="text-xs uppercase tracking-wider text-zinc-500 mb-4 font-['Manrope']">Historial de facturas</h2>
          {paidInvoices.length === 0 ? (
            <div className="text-center py-8 rounded-2xl bg-white/[0.02] border border-white/10">
              <FileText className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 font-['Manrope']">No hay facturas anteriores</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paidInvoices.map(invoice => (
                <InvoiceRow key={invoice.id} invoice={invoice} />
              ))}
            </div>
          )}
        </div>

        {/* Help Note */}
        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 text-center">
          <p className="text-sm text-zinc-400 font-['Manrope']">
            ¿Necesitas ayuda con una factura?{' '}
            <Link to="/help" className="text-white hover:underline">Contacta con nosotros</Link>
          </p>
        </div>
      </main>

      <ScrollToTop />
    </div>
  );
}
